import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@marriott/mi-ui-library';

import { CNAnimatedBannerProps } from './CNAnimatedBanner.types';
import { resolveInitialViewport } from './utils';
import { BannerImage, PreloadImage } from './CNAnimatedBanner.styles';

export const CNAnimatedBanner = ({
  medium,
  linkURL,
  linkTarget,
  ariaLabel,
  className,
  trackingProperties,
}: CNAnimatedBannerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const media = useMemo(() => {
    const viewport = resolveInitialViewport();
    return medium[viewport] ?? null;
  }, []);

  useEffect(() => {
    if (!containerRef.current || isVisible) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [isVisible]);

  if (!media) {
    return null;
  }

  const { poster, animated } = media;

  return (
    <div
      ref={containerRef}
      data-testid="m-cn-homepage-animatedbanner"
      data-component-name="m-cn-homepage-animatedbanner"
    >
      {/* Preload animated media immediately so the swap to `animated` is instant when it becomes visible */}
      <PreloadImage src={animated} alt="" aria-hidden="true" loading="eager" />

      <Button
        isLink
        href={linkURL}
        target={linkTarget}
        ariaLabel={ariaLabel}
        className={className}
        trackingProperties={trackingProperties}
      >
        <BannerImage src={isVisible ? animated : poster} alt="" aria-hidden="true" />
      </Button>
    </div>
  );
};
